import React, { forwardRef } from "react";
import { useSystemId } from "../../../hooks/useReduxSelectors";
import { sendCommand } from "../../../ducks/uiDuck";
import { useDispatch } from "react-redux";
import { setAutoZeroRunManuallyBtnStatus } from "../../../ducks/settingsUIDuck";
import useRoutineStartStopButtonWithCounter from "../../../helpers/useRoutineStartStopButtonWithCounter";
import { Switch, FormControlLabel, Button, Box, Stack, TextField, Typography, CircularProgress } from '@mui/material';


const AutoZeroForm = forwardRef((
  {
    settingsUI,
    autoZeroSettingsFormatted,
    t,
    handleOnChange,
    inputSelected,
  }, ref) => {


  const systemId = useSystemId();
  const dispatch = useDispatch();
  const startButtonDisabled = settingsUI.autoZero.isRunManuallyBtnDisabled;
  const resetButtonDisabled = settingsUI.autoZero.isRunManuallyBtnDisabled;

  const autoZeroRunManuallyButton = (buttonCommand) => {
    if (buttonCommand === "Start") {
      dispatch(setAutoZeroRunManuallyBtnStatus({ systemId, data: { isDisabled: true } }));
      dispatch(
        sendCommand({
          argsProps: {
            HwId: "EdgeDevice",
            Command: "AutoZero",
            Value: "Start",
            purgeEnabled: autoZeroSettingsFormatted.purgeEnabled ? autoZeroSettingsFormatted.purgeEnabled : false,
            sampleLinePurgeEnabled: autoZeroSettingsFormatted.sampleLinePurgeEnabled ? autoZeroSettingsFormatted.sampleLinePurgeEnabled : false
          },
          commandType: "AutoZero",
          systemId
        })
      );
    }
  };

  const resetAlarmManually = () => {
    dispatch(
      sendCommand({
        argsProps: {
          HwId: "EdgeDevice",
          Command: "AutoZero",
          Value: "Reset"
        },
        commandType: "AutoZero",
        systemId
      })
    );
  };

  const { startCounter, stopCounter } = useRoutineStartStopButtonWithCounter(systemId, dispatch, sendCommand, "AutoZero");

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", mb: 2 }}>
        <FormControlLabel
          control={<Switch
            checked={autoZeroSettingsFormatted.inUse ? autoZeroSettingsFormatted.inUse : false}
            onChange={() => handleOnChange(!autoZeroSettingsFormatted.inUse, "inUse")}
          />}
          label={autoZeroSettingsFormatted.inUse ? "Autozero in Use" : "Autozero OFF"}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Switch
              checked={autoZeroSettingsFormatted.purgeEnabled ? autoZeroSettingsFormatted.purgeEnabled : false}
              onChange={() => handleOnChange(!autoZeroSettingsFormatted.purgeEnabled, "purgeEnabled")}
            />}
            label={autoZeroSettingsFormatted.purgeEnabled ? "Purge Probe ON" : "Purge Probe OFF"}
          />
          <FormControlLabel
            control={<Switch
              checked={autoZeroSettingsFormatted.sampleLinePurgeEnabled ? autoZeroSettingsFormatted.sampleLinePurgeEnabled : false}
              onChange={() => handleOnChange(!autoZeroSettingsFormatted.sampleLinePurgeEnabled, "sampleLinePurgeEnabled")}
            />}
            label={autoZeroSettingsFormatted.sampleLinePurgeEnabled ? "Purge Sample line ON" : "Purge Sample line OFF"}
          />
          <Button disabled={resetButtonDisabled} size="medium" onClick={() => resetAlarmManually()}>
            {t("Reset Alarms")}
          </Button>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ alignItems: "center" }}>
        <Typography>{t("AutoZero starts at")}, hh:mm</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            value={autoZeroSettingsFormatted?.startHour ?? ""}
            onChange={(e) => handleOnChange(e.target.value, "startHour")}
            onClick={(e) => inputSelected(e, "startHour")}
            error={autoZeroSettingsFormatted?.startHour !== "" && autoZeroSettingsFormatted?.startHour != null && isNaN(Number(String(autoZeroSettingsFormatted?.startHour).replace(",", ".")))}
            helperText={autoZeroSettingsFormatted?.startHour !== "" && autoZeroSettingsFormatted?.startHour != null && isNaN(Number(String(autoZeroSettingsFormatted?.startHour).replace(",", "."))) ? "Input a number" : ""}
            sx={{ width: 70 }}
          />
          <Typography>:</Typography>
          <TextField
            size="small"
            value={autoZeroSettingsFormatted?.startMinute ?? ""}
            onChange={(e) => handleOnChange(e.target.value, "startMinute")}
            onClick={(e) => inputSelected(e, "startMinute")}
            error={autoZeroSettingsFormatted?.startMinute !== "" && autoZeroSettingsFormatted?.startMinute != null && isNaN(Number(String(autoZeroSettingsFormatted?.startMinute).replace(",", ".")))}
            helperText={autoZeroSettingsFormatted?.startMinute !== "" && autoZeroSettingsFormatted?.startMinute != null && isNaN(Number(String(autoZeroSettingsFormatted?.startMinute).replace(",", "."))) ? "Input a number" : ""}
            sx={{ width: 70 }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button disabled={startButtonDisabled} size="medium" onClick={() => autoZeroRunManuallyButton("Start")}>
            {t("Start")}
          </Button>
          <Button
            disabled={!startButtonDisabled}
            size="medium"
            color="error"
            onMouseDown={startCounter}
            onMouseUp={stopCounter}
            onMouseLeave={stopCounter}
            onTouchStart={startCounter}
            onTouchEnd={stopCounter}
            onTouchCancel={stopCounter}>
            {t("Stop")}
          </Button>
        </Stack>

        {startButtonDisabled && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography>AutoZero running</Typography>
            <CircularProgress size={16} sx={{ color: "common.white" }} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
);



export default AutoZeroForm;
